import { create } from "mutative";
import { withHistory } from "./withHistory";
import { generateUuid } from "../../utils/id";
import type { Body, BodyUUID, Equality, EqualityUUID, Joint } from "../../types/scene";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { EqualityActions, EqualityInput } from "./actionTypes";
import { areBodiesInSameKinematicChain } from "../../utils/bodyHierarchy";
import { logger } from "../../utils/logger";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

type EqualityWithNames = Equality & {
  name1?: unknown;
  name2?: unknown;
};
type JointRecordByString = Record<string, Joint | undefined>;
type BodyRecordByString = Record<string, Body>;
type BodyRecordByUuid = Record<BodyUUID, Body>;

const normalizeSolref = (solref: unknown): [number, number] | undefined => {
  if (!Array.isArray(solref) || solref.length < 2) return undefined;
  const timeconst = Number(solref[0]);
  const dampratio = Number(solref[1]);
  return [
    Math.max(1e-6, Number.isFinite(timeconst) ? timeconst : 0.02),
    Math.max(0, Number.isFinite(dampratio) ? dampratio : 1),
  ];
};

const normalizeSolimp = (solimp: unknown): [number, number, number, number, number] | undefined => {
  if (!Array.isArray(solimp) || solimp.length < 5) return undefined;
  const dmin = clamp(Number.isFinite(Number(solimp[0])) ? Number(solimp[0]) : 0.9, 0, 1);
  const dmax = Math.max(
    dmin,
    clamp(Number.isFinite(Number(solimp[1])) ? Number(solimp[1]) : 0.95, 0, 1)
  );
  const width = Math.max(1e-6, Number.isFinite(Number(solimp[2])) ? Number(solimp[2]) : 0.001);
  const mid = clamp(Number.isFinite(Number(solimp[3])) ? Number(solimp[3]) : 0.5, 0, 1);
  const power = Math.max(1, Number.isFinite(Number(solimp[4])) ? Number(solimp[4]) : 2);
  return [dmin, dmax, width, mid, power];
};

const DEFAULT_POLYCOEF: [number, number, number, number, number] = [0, 1, 0, 0, 0];

const normalizePolycoef = (data: unknown): [number, number, number, number, number] => {
  if (!Array.isArray(data)) return DEFAULT_POLYCOEF;
  const values = data.map(Number);
  if (values.length !== 5 || values.some((value) => !Number.isFinite(value))) {
    return DEFAULT_POLYCOEF;
  }
  return values as [number, number, number, number, number];
};

const normalizeEquality = (input: Equality): Equality => {
  const next: Equality = { ...input };
  const type = next.type;
  if (type === "joint") next.objtype = "joint";
  else if (type === "tendon") next.objtype = "tendon";
  else if (type === "weld") next.objtype = "body";
  else if (type === "connect") {
    // connect supports body or site; default to body
    if (next.objtype !== "body" && next.objtype !== "site") next.objtype = "body";
  }
  if (type === "joint" || type === "tendon") {
    next.data = normalizePolycoef(next.data);
  } else if (type === "connect" && next.objtype === "body") {
    const data = Array.isArray(next.data) ? next.data.map(Number) : [];
    next.data =
      data.length >= 3 && data.slice(0, 3).every(Number.isFinite) ? data.slice(0, 3) : [0, 0, 0];
  } else if (type === "weld") {
    if (Array.isArray(next.data) && next.data.length >= 7) {
      const values = next.data.slice(0, 7).map(Number);
      next.data = values.every(Number.isFinite) ? values : undefined;
    } else if (next.data !== undefined) {
      next.data = undefined;
    }
  }
  const solref = normalizeSolref(next.solref);
  if (solref) next.solref = solref;
  const solimp = normalizeSolimp(next.solimp);
  if (solimp) next.solimp = solimp;
  return next;
};

// MuJoCo rejects equality constraints whose two operands are the same object
// (e.g. weld body A to itself, connect site S to itself). Detect that case
// across every reference field we track so invalid pairs never reach export.
const isSelfReferentialEquality = (eq: Equality): boolean => {
  if (eq.obj1 && eq.obj2 && eq.obj1 === eq.obj2) return true;
  const { name1, name2 } = eq as EqualityWithNames;
  if (name1 && name2 && name1 === name2) return true;
  return false;
};

const isScalarJointReference = (eq: Equality, joints: JointRecordByString): boolean => {
  if (eq.type !== "joint" || eq.objtype !== "joint") return true;
  const joint1 = eq.obj1 ? joints[String(eq.obj1)] : null;
  const joint2 = eq.obj2 ? joints[String(eq.obj2)] : null;
  return [joint1, joint2].every((joint) => {
    if (!joint) return true;
    return joint.kind === "hinge" || joint.kind === "slide";
  });
};

const isBodyEqualityReferenceValid = (eq: Equality, bodies: BodyRecordByString): boolean => {
  if ((eq.type !== "connect" && eq.type !== "weld") || eq.objtype !== "body") return true;
  if (!eq.obj1 || !eq.obj2) return true;
  return !areBodiesInSameKinematicChain(
    bodies as BodyRecordByUuid,
    String(eq.obj1),
    String(eq.obj2)
  );
};

// MuJoCo rejects site-based connect constraints where both sites share a kinematic chain,
// just like body-based connect/weld. Resolve each site to its parent body via body.sites
// arrays and apply the same ancestor check.
const isSiteConnectReferenceValid = (eq: Equality, bodies: BodyRecordByString): boolean => {
  if (eq.type !== "connect" || eq.objtype !== "site") return true;
  if (!eq.obj1 || !eq.obj2) return true;
  const findBodyForSite = (siteUuid: string): string | null => {
    for (const body of Object.values(bodies)) {
      if (!body || !Array.isArray(body.sites)) continue;
      if (body.sites.some((uuid) => uuid === siteUuid)) return String(body.uuid);
    }
    return null;
  };
  const body1 = findBodyForSite(String(eq.obj1));
  const body2 = findBodyForSite(String(eq.obj2));
  if (!body1 || !body2) return true; // parent body unknown — allow through
  return !areBodiesInSameKinematicChain(bodies as BodyRecordByUuid, body1, body2);
};

export const createEqualityActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): EqualityActions => ({
  setEqualities: (equalities: Record<EqualityUUID, Equality> | Equality[]) => {
    set((state) =>
      create(state, (draft) => {
        const list: Equality[] = Array.isArray(equalities)
          ? equalities
          : (Object.values(equalities || {}) as Equality[]);

        const previous: Record<EqualityUUID, Equality> = draft.equality || {};
        const record: Record<EqualityUUID, Equality> = {} as Record<EqualityUUID, Equality>;
        list.forEach((eq) => {
          if (!eq?.uuid) return;
          const normalized = normalizeEquality(eq);
          if (isSelfReferentialEquality(normalized)) {
            // Preserve the prior valid entry, when present, so in-flight edits don't
            // silently delete a constraint when obj1 briefly equals obj2.
            const prior = previous[normalized.uuid];
            if (prior) {
              logger.warn(
                `[equalityActions] Rejected self-referential update for "${normalized.name ?? normalized.uuid}" (${normalized.type}); keeping previous value.`
              );
              record[normalized.uuid] = prior;
            } else {
              logger.warn(
                `[equalityActions] Dropped self-referential equality "${normalized.name ?? normalized.uuid}" (${normalized.type}).`
              );
            }
            return;
          }
          if (!isScalarJointReference(normalized, draft.joints || {})) {
            logger.warn(
              `[equalityActions] Dropped joint equality "${normalized.name ?? normalized.uuid}" because MuJoCo equality/joint only supports hinge or slide joints.`
            );
            return;
          }
          if (!isBodyEqualityReferenceValid(normalized, draft.bodies || {})) {
            logger.warn(
              `[equalityActions] Dropped body equality "${normalized.name ?? normalized.uuid}" because body connect/weld constraints must target different kinematic branches.`
            );
            return;
          }
          if (!isSiteConnectReferenceValid(normalized, draft.bodies || {})) {
            logger.warn(
              `[equalityActions] Dropped site-connect equality "${normalized.name ?? normalized.uuid}" because both sites are on the same kinematic chain.`
            );
            return;
          }
          record[normalized.uuid] = normalized;
        });
        draft.equality = record;
      })
    );
  },

  addEquality: withHistory((equalityInput: EqualityInput): EqualityUUID | null => {
    const uuid = (equalityInput.uuid ?? generateUuid()) as EqualityUUID;
    const existingNames = namesFromRecord(get().equality);
    const name =
      typeof equalityInput.name === "string" && equalityInput.name.trim()
        ? nextUniqueName(equalityInput.name, existingNames)
        : undefined;
    const equality = normalizeEquality({ ...equalityInput, name, uuid } as Equality);

    if (isSelfReferentialEquality(equality)) {
      logger.error(
        `[equalityActions] Rejected self-referential equality (${equality.type}): obj1 and obj2 must be different.`
      );
      return null;
    }
    if (!isScalarJointReference(equality, get().joints || {})) {
      logger.error(
        `[equalityActions] Rejected joint equality (${equality.name ?? uuid}): MuJoCo equality/joint only supports hinge or slide joints.`
      );
      return null;
    }
    if (!isBodyEqualityReferenceValid(equality, get().bodies || {})) {
      logger.error(
        `[equalityActions] Rejected body equality (${equality.name ?? uuid}): connect/weld constraints must target different kinematic branches.`
      );
      return null;
    }
    if (!isSiteConnectReferenceValid(equality, get().bodies || {})) {
      logger.error(
        `[equalityActions] Rejected site-connect equality (${equality.name ?? uuid}): both sites must be on different kinematic branches.`
      );
      return null;
    }

    set((state) =>
      create(state, (draft) => {
        draft.equality[uuid] = equality;
      })
    );
    return uuid;
  }),

  updateEquality: withHistory((equalityUuid: EqualityUUID, patch: Partial<Equality>): void => {
    set((state) =>
      create(state, (draft) => {
        const current = draft.equality?.[equalityUuid];
        if (!current) return;
        const next = normalizeEquality({ ...current, ...patch, uuid: equalityUuid } as Equality);
        if (isSelfReferentialEquality(next)) {
          logger.error(
            `[equalityActions] Rejected self-referential update for "${next.name ?? equalityUuid}" (${next.type}).`
          );
          return;
        }
        if (!isScalarJointReference(next, draft.joints || {})) {
          logger.error(
            `[equalityActions] Rejected joint equality update for "${next.name ?? equalityUuid}": MuJoCo equality/joint only supports hinge or slide joints.`
          );
          return;
        }
        if (!isBodyEqualityReferenceValid(next, draft.bodies || {})) {
          logger.error(
            `[equalityActions] Rejected body equality update for "${next.name ?? equalityUuid}": connect/weld constraints must target different kinematic branches.`
          );
          return;
        }
        if (!isSiteConnectReferenceValid(next, draft.bodies || {})) {
          logger.error(
            `[equalityActions] Rejected site-connect update for "${next.name ?? equalityUuid}": both sites must be on different kinematic branches.`
          );
          return;
        }
        draft.equality[equalityUuid] = next;
      })
    );
  }),

  removeEquality: withHistory((equalityUuid: EqualityUUID): void => {
    set((state) =>
      create(state, (draft) => {
        if (!draft.equality) return;
        delete draft.equality[equalityUuid];
      })
    );
  }),
});

/**
 * Utility functions for working with equality constraints
 */
export const equalityUtils = {
  /**
   * Check if two bodies are connected via a connect constraint
   * @param equalities Record of equality constraints
   * @param body1Uuid First body UUID
   * @param body2Uuid Second body UUID
   * @returns The connect constraint if found, null otherwise
   */
  findConnectConstraint: (
    equalities: Record<EqualityUUID, Equality> | undefined,
    body1Uuid: string,
    body2Uuid: string
  ) => {
    if (!equalities) return null;

    const equalityArray = Object.values(equalities);
    return (
      equalityArray.find((eq) => {
        if (eq.type !== "connect" || eq.objtype !== "body") return false;

        return (
          (eq.obj1 === body1Uuid && eq.obj2 === body2Uuid) ||
          (eq.obj1 === body2Uuid && eq.obj2 === body1Uuid)
        );
      }) || null
    );
  },

  /**
   * Get all connect constraints for a specific body
   * @param equalities Record of equality constraints
   * @param bodyUuid Body UUID to search for
   * @returns Array of connect constraints involving this body
   */
  getConnectConstraintsForBody: (
    equalities: Record<EqualityUUID, Equality> | undefined,
    bodyUuid: string
  ) => {
    if (!equalities) return [];

    const equalityArray = Object.values(equalities);

    return equalityArray.filter((eq) => {
      if (eq.type !== "connect" || eq.objtype !== "body") return false;

      return eq.obj1 === bodyUuid || eq.obj2 === bodyUuid;
    });
  },

  /**
   * Get all connect constraints
   * @param equalities Record of equality constraints
   * @returns Array of connect constraints
   */
  getAllConnectConstraints: (equalities: Record<EqualityUUID, Equality> | undefined) => {
    if (!equalities) return [];

    const equalityArray = Object.values(equalities);
    return equalityArray.filter((eq) => eq.type === "connect" && eq.objtype === "body");
  },
};
